import React, {useRef, useState} from 'react';
import {Link, useNavigate, useParams} from 'react-router-dom';
import {useProjects} from '../../ProjectsContext';
import ImageManager from './ImageManager';
import * as s from './adminStyles';

const API_BASE = '/api';

const emptyProject = {
    title: '',
    description: '',
    images: [''],
    position: [0, 0, -3],
    tags: [],
};

export default function ProjectForm() {
    const {id} = useParams();
    const {projects} = useProjects();
    const existing = id ? projects.find((p) => p.id === Number(id)) : null;

    if (id && !existing) {
        return (
            <div style={s.page}>
                <div style={s.container}>
                    <p>Projekt nicht gefunden.</p>
                    <Link to="/intern" style={s.button}>Zurück zur Liste</Link>
                </div>
            </div>
        );
    }

    return <FormFields initial={existing ?? emptyProject} isNew={!existing}/>;
}

function FormFields({initial, isNew}) {
    const navigate = useNavigate();
    const {saveProject, nextId} = useProjects();
    const fileInputRef = useRef(null);
    const pendingImageIndex = useRef(null);

    const [title, setTitle] = useState(initial.title);
    const [description, setDescription] = useState(initial.description);
    const [images, setImages] = useState(initial.images?.length ? initial.images : ['']);
    const [position, setPosition] = useState(initial.position.map(String));
    const [tagsText, setTagsText] = useState((initial.tags ?? []).join(', '));
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const [uploadingIndex, setUploadingIndex] = useState(null);
    const [showImageManager, setShowImageManager] = useState(false);

    const setImage = (i, value) => setImages((list) => list.map((img, idx) => (idx === i ? value : img)));
    const removeImage = (i) => setImages((list) => (list.length > 1 ? list.filter((_, idx) => idx !== i) : ['']));
    const setPos = (i, value) => setPosition((p) => p.map((v, idx) => (idx === i ? value : v)));

    const triggerUpload = (index) => {
        pendingImageIndex.current = index;
        fileInputRef.current?.click();
    };

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = ''; // damit dieselbe Datei erneut gewählt werden kann
        if (!file) return;

        const index = pendingImageIndex.current;
        setUploadingIndex(index);
        setError('');

        try {
            const formData = new FormData();
            formData.append('image', file);
            const res = await fetch(`${API_BASE}/upload.php`, {
                method: 'POST',
                credentials: 'include',
                body: formData,
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data.ok) throw new Error(data.error || 'Upload fehlgeschlagen.');
            setImage(index, data.url);
        } catch (err) {
            setError(err.message || 'Upload fehlgeschlagen.');
        } finally {
            setUploadingIndex(null);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();

        const cleanImages = images.map((u) => u.trim()).filter(Boolean);
        const numbers = position.map((v) => Number(v));

        if (!title.trim()) return setError('Bitte einen Titel angeben.');
        if (cleanImages.length === 0) return setError('Bitte mindestens ein Bild angeben.');
        if (numbers.some((n) => Number.isNaN(n))) return setError('Position muss aus Zahlen bestehen.');

        setSaving(true);
        setError('');
        try {
            await saveProject({
                id: isNew ? nextId() : initial.id,
                title: title.trim(),
                description: description.trim(),
                images: cleanImages,
                position: numbers,
                tags: tagsText.split(',').map((t) => t.trim()).filter(Boolean),
            });
            navigate('/intern');
        } catch (err) {
            setError(err.message || 'Speichern fehlgeschlagen.');
            setSaving(false);
        }
    };

    return (
        <div style={s.page}>
            {/* Ein verstecktes File-Input für alle Upload-Buttons gemeinsam */}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                style={{display: 'none'}}
                onChange={handleFileChange}
            />

            <form onSubmit={handleSave} style={s.container}>
                <div style={s.headerRow}>
                    <h1 style={s.h1}>{isNew ? 'Neues Projekt' : 'Projekt bearbeiten'}</h1>
                </div>

                <label style={s.label}>Titel</label>
                <input style={s.input} value={title} onChange={(e) => setTitle(e.target.value)}/>

                <label style={s.label}>Beschreibung</label>
                <textarea
                    style={{...s.input, minHeight: '140px', resize: 'vertical'}}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                />

                <label style={s.label}>Bilder</label>
                {images.map((url, i) => (
                    <div key={i} style={{display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center'}}>
                        {url && (
                            <img
                                src={url}
                                alt=""
                                style={{
                                    width: '40px',
                                    height: '40px',
                                    objectFit: 'cover',
                                    borderRadius: '6px',
                                    background: '#f5f5f5',
                                    flexShrink: 0
                                }}
                            />
                        )}
                        <input
                            style={s.input}
                            placeholder="https://… oder Datei hochladen"
                            value={url}
                            onChange={(e) => setImage(i, e.target.value)}
                        />
                        <button
                            type="button"
                            style={{...s.button, whiteSpace: 'nowrap'}}
                            onClick={() => triggerUpload(i)}
                            disabled={uploadingIndex === i}
                        >
                            {uploadingIndex === i ? 'Lädt…' : 'Hochladen'}
                        </button>
                        <button type="button" style={s.button} onClick={() => removeImage(i)}
                                aria-label="Bild entfernen">
                            ×
                        </button>
                    </div>
                ))}

                <div style={{display: 'flex', gap: '8px', flexWrap: 'wrap'}}>
                    <button type="button" style={s.button} onClick={() => setImages((l) => [...l, ''])}>
                        + Bild hinzufügen
                    </button>
                    <button type="button" style={s.button} onClick={() => setShowImageManager(true)}>
                        Aus hochgeladenen Bildern wählen / verwalten
                    </button>
                </div>
                <p style={s.hint}>Das erste Bild ist zugleich der Stern im Universum.</p>

                <label style={s.label}>Position im Raum (x, y, z)</label>
                <div style={{display: 'flex', gap: '8px'}}>
                    {['x', 'y', 'z'].map((axis, i) => (
                        <input
                            key={axis}
                            style={s.input}
                            type="number"
                            step="0.5"
                            placeholder={axis}
                            value={position[i]}
                            onChange={(e) => setPos(i, e.target.value)}
                        />
                    ))}
                </div>
                <p style={s.hint}>Feintuning – bequemer geht's im visuellen Editor ("Projekt Positionen ändern").</p>

                {error && <p style={{...s.hint, color: '#c0392b', marginTop: '16px'}}>{error}</p>}

                <div style={{display: 'flex', gap: '8px', marginTop: '32px'}}>
                    <Link to="/intern" style={s.button}>Abbrechen</Link>
                    <button type="submit" style={{...s.primaryButton, opacity: saving ? 0.6 : 1}} disabled={saving}>
                        {saving ? 'Speichert…' : 'Speichern'}
                    </button>
                </div>
            </form>

            {showImageManager && (
                <ImageManager
                    onClose={() => setShowImageManager(false)}
                    onPick={(url) => {
                        setImages((list) => (list[list.length - 1] === '' ? [...list.slice(0, -1), url] : [...list, url]));
                        setShowImageManager(false);
                    }}
                />
            )}
        </div>
    );
}
